import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

type AnalyticsWindow = Window & {
  __clevertapInitialized?: boolean;
  clevertap?: {
    getAccountID?: () => string;
    event?: {
      push?: (event: string, eventData: Record<string, string>) => void;
    };
  };
};

type QueuedCleverTapEvent = {
  event: string;
  eventData: Record<string, string>;
};

const CLEVERTAP_READY_EVENT = "clevertap:ready";
const MAX_QUEUED_EVENTS = 100;

let queuedEvents: QueuedCleverTapEvent[] = [];
let isReadyListenerBound = false;

const isClevertapReady = (analyticsWindow: AnalyticsWindow): boolean => {
  return Boolean(
    analyticsWindow.clevertap
      && (analyticsWindow.clevertap.getAccountID || analyticsWindow.__clevertapInitialized),
  );
};

const flushQueuedEvents = (): void => {
  const analyticsWindow = window as AnalyticsWindow;
  if (!isClevertapReady(analyticsWindow)) {
    return;
  }

  const queuedEventsToFlush = queuedEvents;
  queuedEvents = [];

  queuedEventsToFlush.forEach(({ event, eventData }) => {
    analyticsWindow.clevertap?.event?.push?.(event, eventData);
  });
};

const bindClevertapReadyListener = (): void => {
  if (isReadyListenerBound) {
    return;
  }

  window.addEventListener(CLEVERTAP_READY_EVENT, flushQueuedEvents);
  isReadyListenerBound = true;
};

const enqueueClevertapEvent = (
  event: string,
  eventData: Record<string, string>,
): void => {
  if (queuedEvents.length >= MAX_QUEUED_EVENTS) {
    queuedEvents.shift();
  }

  queuedEvents.push({ event, eventData });
  bindClevertapReadyListener();
};

const pushClevertapEvent = (
  event: string,
  eventData: Record<string, string>,
): void => {
  const analyticsWindow = window as AnalyticsWindow;
  if (isClevertapReady(analyticsWindow)) {
    analyticsWindow.clevertap?.event?.push?.(event, eventData);
    return;
  }

  if (!analyticsWindow.clevertap) {
    enqueueClevertapEvent(event, eventData);
    return;
  }

  enqueueClevertapEvent(event, eventData);
};

export const clevertapEvent = (
  event: string,
  eventData: Record<string, string>,
): void => {
  const filteredEventData = Object.fromEntries(
    Object.entries(eventData).filter(([, value]) => value !== "NA"),
  );

  if (isOpenInApp()) {
    window.prompt(
      `triggerCTEvent://${JSON.stringify({ eventName: event, props: filteredEventData })}`,
    );
    return;
  }

  pushClevertapEvent(event, filteredEventData);
};